import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO_LINES } from "../data/site";
import { lineReveal, fadeUp } from "../lib/animations";

const heroImg =
  "https://images.pexels.com/photos/7561899/pexels-photo-7561899.jpeg?auto=compress&cs=tinysrgb&w=1400";
const heroImg2 =
  "https://images.unsplash.com/photo-1576633587382-13ddf37b1fc1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export const KineticHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-white pt-28 sm:pt-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="flex items-center gap-3"
        >
          <span className="h-2 w-2 rounded-full bg-brand-accent animate-pulse" />
          <span className="text-xs sm:text-sm uppercase tracking-[0.22em] font-semibold text-brand-ink/50">
            Million Words — Immersive Media Studio
          </span>
        </motion.div>

        <div className="mt-8 sm:mt-10 grid grid-cols-12 gap-6 items-end">
          <h1 className="col-span-12 lg:col-span-9 font-heading font-extrabold tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-[6.2rem] text-brand-ink">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="visible"
                  className={`block ${i === 3 ? "text-brand" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial="hidden"
            animate="visible"
            custom={4}
            variants={fadeUp}
            className="col-span-12 lg:col-span-3 pb-3"
          >
            <p className="text-base sm:text-lg leading-relaxed text-brand-ink/60">
              We craft Extended Reality, digital learning and creative
              production that makes knowledge unforgettable.
            </p>
            <a
              href="#contact"
              data-testid="hero-cta"
              className="mt-6 group inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand-ink transition-colors duration-200"
            >
              Start a project
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 1 }}
          className="mt-14 sm:mt-16 grid grid-cols-12 gap-4 sm:gap-6"
        >
          <div className="col-span-12 md:col-span-8 relative h-[340px] sm:h-[460px] overflow-hidden rounded-2xl border border-gray-200 bg-brand-surface">
            <motion.img
              src={heroImg}
              alt="Immersive XR experience"
              style={{ y: y1, scale }}
              className="absolute inset-0 h-[130%] w-full object-cover"
            />
            <div className="absolute left-5 bottom-5 rounded-full bg-white/80 backdrop-blur-md px-4 py-2 text-xs font-semibold uppercase tracking-widest text-brand-ink">
              Extended Reality
            </div>
          </div>
          <div className="col-span-12 md:col-span-4 relative h-[340px] sm:h-[460px] overflow-hidden rounded-2xl border border-gray-200 bg-brand-surface">
            <motion.img
              src={heroImg2}
              alt="Virtual reality headset"
              style={{ y: y2 }}
              className="absolute inset-0 h-[130%] w-full object-cover"
            />
            <div className="absolute left-5 bottom-5 rounded-full bg-brand-ink/80 backdrop-blur-md px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white">
              Immersive Training
            </div>
          </div>
        </motion.div>
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 mt-10 pb-6 flex items-center gap-2 text-brand-ink/40">
        <ArrowDown className="h-4 w-4 animate-bounce" />
        <span className="text-xs uppercase tracking-[0.2em]">Scroll to explore</span>
      </div>
    </section>
  );
};
